import React, { useState } from 'react';

import { Button, Input, Text, FormAlert, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

import type { IChangePasswordDialogProps } from './ChangePasswordDialog.type';

import api from '../../../utils/axios';

export const ChangePasswordPanel: React.FC<IChangePasswordDialogProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateForm = (): boolean => {
    if (!currentPassword) {
      setError('Current password is required');
      return false;
    }
    if (!newPassword) {
      setError('New password is required');
      return false;
    }

    
    if (newPassword === currentPassword) {
      setError('New password must be different from current password');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      
      setSuccess(true);
      
      // Show success message briefly then close
      setTimeout(() => {
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      }, 2000);
    } catch (err: any) {
      let errorMessage = 'Failed to change password. Please try again.';
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data) {
        errorMessage = typeof err.response.data === 'string' ? err.response.data : errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        setConfirmPasswordTouched(false);
        setSuccess(false);
        onClose?.();
      }, 300);
    }
  };

  const clearError = () => {
    if (error) setError('');
  };

  // Get confirm password error message
  const getConfirmPasswordError = (): string => {
    if (!confirmPasswordTouched) return '';
    if (!confirmPassword) return 'Please confirm your password';
    if (confirmPassword !== newPassword) return 'Passwords do not match';
    return '';
  };

  // Check if form is valid for enabling/disabling submit button
  const isFormValid = () => {
    return (
      currentPassword.trim() !== '' &&
      newPassword.trim() !== '' &&
      confirmPassword.trim() !== '' &&
      newPassword === confirmPassword &&
      newPassword !== currentPassword
    );
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div 
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} 
      onClick={handleClose}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[400px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Top Section */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <div>
            <Text variant="text-xl" weight="bold">Change Password</Text>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
            disabled={loading}
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1">
          {/* Middle Section */}
          <div className="flex flex-col items-center gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto flex-1">
            {/* Success Message */}
            {success && (
              <FormAlert
                message="Password changed successfully!"
                variant="success"
              />
            )}

            {/* Error Message */}
            {error && (
              <FormAlert
                message={error}
                variant="error"
                onDismiss={clearError}
              />
            )}

            <Text variant="text-sm" color="secondary" as="p" className="w-full">
              Please enter your current password and choose a new secure password.
            </Text>

            <div className="w-full space-y-6">
              {/* Current Password */}
              <Input
                id="currentPassword"
                name="currentPassword"
                type={showCurrentPassword ? 'text' : 'password'}
                label="Current Password"
                startIcon={<Icon name={IconName.Lock} size="md" />}
                endIcon={<Icon name={showCurrentPassword ? IconName.EyeOpen : IconName.EyeClosed} size="md" />}
                onEndIconClick={() => setShowCurrentPassword(!showCurrentPassword)}
                placeholder="Enter your current password"
                required
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  clearError();
                }}
                disabled={loading || success}
                autoComplete="current-password"
                autoFocus
                className="w-full"
              />

              {/* New Password */}
              <Input
                id="newPassword"
                name="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                startIcon={<Icon name={IconName.Lock} size="md" />}
                endIcon={<Icon name={showNewPassword ? IconName.EyeOpen : IconName.EyeClosed} size="md" />}
                onEndIconClick={() => setShowNewPassword(!showNewPassword)}
                label="New Password"
                placeholder="Enter your new password"
                required
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  clearError();
                }}
                disabled={loading || success}
                autoComplete="new-password"
                showStrength={true}
                className="w-full"
              />

              {/* Confirm Password */}
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                label="Confirm New Password"
                startIcon={<Icon name={IconName.Lock} size="md" />}
                endIcon={!getConfirmPasswordError() && <Icon name={showConfirmPassword ? IconName.EyeOpen : IconName.EyeClosed} size="md" />}
                onEndIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
                placeholder="Confirm your new password"
                required
                errorText={getConfirmPasswordError()}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setConfirmPasswordTouched(true);
                  clearError();
                }}
                onBlur={() => setConfirmPasswordTouched(true)}
                disabled={loading || success}
                autoComplete="new-password"
                className="w-full"
              />
            </div>
          </div>

          {/* Bottom Section */}
          <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
            <Button
              variant="outline"
              label="Cancel"
              type="button"
              onClick={handleClose}
              disabled={loading}
            />
            <Button
              variant="primary"
              type="submit"
              label={loading ? 'Changing...' : 'Change Password'}
              disabled={loading || success || !isFormValid()}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

